import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Box from '@mui/material/Box';

import { goToMenu } from 'bento-ordering/cart/actions/orderSettings';
import ModalCloseButton from 'bento-ordering/base/ui_components/ModalCloseButton';
import PrimaryCta from 'components/Buttons/PrimaryCta';
import {
  GROUP_SETTINGS_TITLE,
  GROUP_SETTINGS_CTA_LABEL,
  START_NEW_ORDER,
  UPDATE_GROUP_SETTINGS_CTA_LABEL,
  UPDATE_GROUP_SETTINGS_TITLE,
  GROUP_ORDER_DINER_NAME_INPUT_MAX_LENGTH
} from 'bento-ordering/group_ordering/constants/groupOrder';
import { GROUP_ORDER_INVITE } from 'bento-ordering/group_ordering/constants/paths';
import { CART_BASE } from 'bento-ordering/cart/constants/paths';
import {
  createGroupOrder,
  updateGroupOrder
} from 'bento-ordering/group_ordering/actions';
import { useRedirectToOOMenu } from 'bento-ordering/group_ordering/hooks';
import {
  StyledGroupOrderWrapper,
  TitleTypography,
  StyledInputName
} from 'bento-ordering/group_ordering/ui_components/GroupOrder';
import GroupOrderSpendingLimit from 'bento-ordering/group_ordering/components/GroupOrderSpendingLimit';
import GroupOrderDeadlineControl from 'bento-ordering/group_ordering/components/GroupOrderDeadlineControl';
import GroupOrderErrorToast from 'bento-ordering/group_ordering/components/GroupOrderErrorToast';
import HowItWorks from 'bento-ordering/group_ordering/components/HowItWorks';
import {
  formatDate,
  formatExistingDateString,
  formatTimeFromDateString
} from 'bento-ordering/group_ordering/utils';
import { DATE_YYYY_MM_DD_FORMAT } from 'bento-ordering/base/constants/timeFormat';
import {
  selectCartGroupOrderAdminName,
  selectCartGroupOrderSlug,
  selectCartGroupOrderDeadline,
  selectCartIsGroupOrder,
  selectGroupOrderErrorIs404
} from 'bento-ordering/cart/selectors/index';

const GroupOrderSettings = () => {
  const [nameInput, setNameInput] = useState('');
  const [inputDeadlineObj, setInputDeadlineObj] = useState({
    date: '',
    time: ''
  });
  const [existingDeadlineObj, setExistingDeadlineObj] = useState({
    date: '',
    time: ''
  });
  const [userUpdatedDate, setUserUpdatedDate] = useState(false);
  const [userUpdatedTime, setUserUpdatedTime] = useState(false);
  const [spendingLimit, setSpendingLimit] = useState('');
  const [customLimit, setCustomLimit] = useState();
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  // A GO cart in state means we're in update settings mode, not create settings mode
  const isUpdateSettings = useSelector(selectCartIsGroupOrder);
  const groupOrderName = useSelector(selectCartGroupOrderAdminName);
  const existingGroupOrderSlug = useSelector(selectCartGroupOrderSlug);
  const existingDeadline = useSelector(selectCartGroupOrderDeadline);
  const errorIs404NotFound = useSelector(selectGroupOrderErrorIs404);

  const dispatch = useDispatch();
  const history = useHistory();

  const redirectToOO = useRedirectToOOMenu();

  useEffect(() => {
    if (existingDeadline) {
      const existingDate = formatExistingDateString(existingDeadline);
      const existingTime = formatTimeFromDateString(existingDeadline);
      setExistingDeadlineObj({
        date: existingDate,
        time: existingTime
      });
    }
  }, [existingDeadline]);

  const userUpdatedDeadline = userUpdatedDate || userUpdatedTime;
  const isCustomLimitOutOfRange = customLimit < 0 || customLimit > 500;
  const isCustomLimitValid =
    !isCustomLimitOutOfRange || customLimit === undefined;

  const isInputDeadlineInValidForNewSettings = () => {
    // invalid for new settings: if one of time or date is '' (can be both '' which means no deadline).
    const { date, time } = inputDeadlineObj;
    return (!date && !!time) || (!!date && !time);
  };

  const isInputDeadlineInValidForUpdateSettings = () => {
    // invalid for update settings: if user updated both date & time but one of them is ''.
    // they can update only date or time and leave the other one '', we'll just take the value from existing deadline)
    const { date, time } = inputDeadlineObj;
    return (
      userUpdatedDate &&
      userUpdatedTime &&
      ((!date && !!time) || (!!date && !time))
    );
  };

  const shouldDisableCTA = () => {
    /*
    Update Settings: disable initially (when no change has been made by user) 
                     or deadline is changed into something invalid
    New Settings: disable when no name input
                  or invalid custom limit
                  or invalid deadline
    */
    if (isUpdateSettings) {
      return !userUpdatedDeadline || isInputDeadlineInValidForUpdateSettings();
    }
    // if not isUpdateSettings, then must be new settings
    return (
      !nameInput.trim() ||
      !isCustomLimitValid ||
      isInputDeadlineInValidForNewSettings()
    );
  };

  const handleNameChange = event => {
    setNameInput(event.target.value);
  };

  const handleFormatDate = () => {
    // use existing date when user updated only time field
    const dateToFormat = userUpdatedDate
      ? inputDeadlineObj.date
      : existingDeadlineObj.date;
    return dateToFormat
      ? formatDate(dateToFormat, DATE_YYYY_MM_DD_FORMAT)
      : null;
  };

  const handleFormatTime = () => {
    // use existing time when user updated only date field
    const timeToFormat = userUpdatedTime
      ? inputDeadlineObj.time
      : existingDeadlineObj.time;
    return timeToFormat.trim() ? timeToFormat : null;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    setIsSubmittingForm(true);

    const result = await dispatch(
      createGroupOrder({
        name: nameInput,
        per_person_spending_limit: spendingLimit,
        ordering_deadline_date: handleFormatDate(),
        ordering_deadline_time: handleFormatTime()
      })
    );

    setIsSubmittingForm(false);

    if (result?.error !== true) {
      const groupOrderSlug = result?.payload?.group_order?.slug;
      history.replace(`${GROUP_ORDER_INVITE}?group_order=${groupOrderSlug}`);
    }
  };

  const handleUpdate = async e => {
    e.preventDefault();

    const result = await dispatch(
      updateGroupOrder(existingGroupOrderSlug, {
        ordering_deadline_date: handleFormatDate(),
        ordering_deadline_time: handleFormatTime()
      })
    );

    if (result?.error !== true) {
      history.replace(`${CART_BASE}?group_order=${existingGroupOrderSlug}`);
    }
  };

  const startOverCTAButton = (
    <PrimaryCta handleClick={redirectToOO} fullWidth label={START_NEW_ORDER} />
  );

  return (
    <>
      <ModalCloseButton autoFocus handleClick={() => dispatch(goToMenu())} />
      <form onSubmit={isUpdateSettings ? handleUpdate : handleSubmit}>
        <StyledGroupOrderWrapper>
          <TitleTypography>
            {isUpdateSettings
              ? UPDATE_GROUP_SETTINGS_TITLE
              : GROUP_SETTINGS_TITLE}
          </TitleTypography>
          <HowItWorks />
          <Box mt="16px" mb="35px">
            <StyledInputName
              variant="outlined"
              placeholder="Your Name"
              label=""
              onChange={handleNameChange}
              value={nameInput || groupOrderName}
              inputProps={{
                maxLength: GROUP_ORDER_DINER_NAME_INPUT_MAX_LENGTH,
                'aria-required': true
              }}
              required
              autoFocus
              disabled={isUpdateSettings}
            />
          </Box>
          <GroupOrderSpendingLimit
            onSpendingLimitChange={setSpendingLimit}
            customLimit={customLimit}
            setCustomLimit={setCustomLimit}
            isCustomLimitValid={isCustomLimitValid}
            disabled={isUpdateSettings}
          />
          <GroupOrderDeadlineControl
            inputDeadlineObj={inputDeadlineObj}
            existingDeadlineObj={existingDeadlineObj}
            setInputDeadlineObj={setInputDeadlineObj}
            startExpand={isUpdateSettings}
            userUpdatedDate={userUpdatedDate}
            userUpdatedTime={userUpdatedTime}
            setUserUpdatedDate={setUserUpdatedDate}
            setUserUpdatedTime={setUserUpdatedTime}
          />
          <GroupOrderErrorToast />
          {errorIs404NotFound ? (
            startOverCTAButton
          ) : (
            <PrimaryCta
              disabled={shouldDisableCTA() || isSubmittingForm}
              handleClick={() => {}}
              type="submit"
              fullWidth
              label={
                isUpdateSettings
                  ? UPDATE_GROUP_SETTINGS_CTA_LABEL
                  : GROUP_SETTINGS_CTA_LABEL
              }
            />
          )}
        </StyledGroupOrderWrapper>
      </form>
    </>
  );
};

export default GroupOrderSettings;
